import React, { useEffect, useState } from "react";
import { checkHealth, abortTrade, resetBot } from "./services/api.js";
import { getConfig } from "./config.js";
import { ToastProvider, useToasts } from "./components/ToastHost.js";
import CapitalWidget from "./components/CapitalWidget.js";
import EquityWidget from "./components/EquityWidget.js";
import EquityChart from "./components/EquityChart.js";
import PerformanceSummary from "./components/PerformanceSummary.js";
import SignalPreview from "./components/SignalPreview.js";
import SimulationPanel from "./components/SimulationPanel.js";
import ExportPanel from "./components/ExportPanel.js";
import TradeHistory from "./components/TradeHistory.js";
import CriticalOverlay from "./components/CriticalOverlay.js";
import VirtualBotPanel from "./components/VirtualBotPanel.js";

function Shell(){
  const [period, setPeriod] = useState("month");
  const [health, setHealth] = useState({ status: "unknown" });
  const [critical, setCritical] = useState({ visible: false, message: "" });
  const { push } = useToasts();
  const cfg = getConfig();

  useEffect(()=>{
    let stop = false;
    async function poll(){
      try{
        const h = await checkHealth();
        if (!stop) setHealth(h);
      }catch{
        if (!stop) setHealth({ status: "error" });
      }finally{
        if (!stop) setTimeout(poll, cfg.HEALTH_POLL_MS || 60000);
      }
    }
    poll();
    return () => { stop = true; };
  },[]);

  const abortAll = async () => {
    try{ await abortTrade({ all: true }); push("Abbruch ausgelöst"); }
    catch(e){ push(`Fehler: ${e.message}`,"error"); }
  };
  const doReset = async () => {
    try{ await resetBot(); push("Bot wurde zurückgesetzt"); }
    catch(e){ push(`Fehler: ${e.message}`,"error"); }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <CriticalOverlay visible={critical.visible} message={critical.message} onClose={()=>setCritical({visible:false,message:""})} />
      <div className="max-w-7xl mx-auto p-4 space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-2xl md:text-3xl font-bold">TBGPT Trading Dashboard</h1>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded ${health.status==='ok'?'bg-green-100 text-green-700':health.status==='error'?'bg-red-100 text-red-700':'bg-gray-200'}`}>
              Health: {health.status}
            </span>
            <button className="btn btn-muted" onClick={doReset}>Bot reset</button>
            <button className="btn btn-danger" onClick={abortAll}>Alle Trades abbrechen</button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CapitalWidget setCritical={setCritical} />
          <EquityWidget period={period} />
        </div>

        <div className="flex flex-wrap gap-2">
          {['day','week','month','quarter','year'].map(p=> (
            <button key={p} onClick={()=>setPeriod(p)} className={`btn ${period===p?'btn-primary':'btn-muted'}`}>{p}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 card">
            <div className="mb-2 font-semibold">Equity-Verlauf</div>
            <EquityChart period={period} />
          </div>
          <PerformanceSummary period={period} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <SignalPreview />
          </div>
          <SimulationPanel />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ExportPanel />
          <TradeHistory />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <VirtualBotPanel />
        </div>
      </div>
    </div>
  );
}

export default function App(){
  return (
    <ToastProvider>
      <Shell />
    </ToastProvider>
  );
}
